// speech — the device voice layer for Ask BigAgri (spec P1).
//
// Everything voice happens ON the device: SpeechRecognition (push-to-talk in)
// and speechSynthesis (sentence-chunked answers out). No audio ever reaches
// our servers — only the final text transcript does. PTT only, never open
// mic: recognition runs solely between start() and stop().
//
// Browser reality: SpeechRecognition is prefixed on iOS/Safari
// (webkitSpeechRecognition) and absent in some WebViews/Firefox — callers
// must gate on speechInputSupported() and hide the mic when false.

import { useEffect, useRef, useState } from 'react'
import { playTone } from '@/lib/tone'

// Minimal ambient surface — lib.dom doesn't type the prefixed constructor.
interface RecognitionAlternative { transcript: string; confidence?: number }
interface RecognitionResult { 0: RecognitionAlternative; isFinal: boolean }
interface RecognitionEvent { resultIndex: number; results: ArrayLike<RecognitionResult> }
interface RecognitionErrorEvent { error: string }
interface Recognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((e: RecognitionEvent) => void) | null
  onerror: ((e: RecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}
type RecognitionCtor = new () => Recognition

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

function speechInputSupported(): boolean {
  return recognitionCtor() !== null
}

export function speechOutputSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

// iOS/Safari refuses to start speechSynthesis unless the FIRST utterance is
// spoken synchronously inside a user gesture — our real sentences arrive
// seconds later from the SSE stream, so without priming the phone stays
// silent. Call this in the gesture handler that triggers an ask (mic press,
// Ask select, floor button press): it speaks a silent utterance to unlock
// audio and resume()s a wedged queue (the cancel-then-speak Safari quirk).
export function primeSpeechOutput(): void {
  if (!speechOutputSupported()) return
  const s = window.speechSynthesis
  s.resume()
  const u = new SpeechSynthesisUtterance(' ')
  u.volume = 0
  s.speak(u)
}

// Short, quiet "ask submitted" acknowledgement tone (PTT release).
export function playAckTone(): void {
  playTone({ freq: 660, ms: 120, gain: 0.05 })
}

// ---------------------------------------------------------------------------
// Sentence chunking — pure, unit-tested. The stream speaks each COMPLETE
// sentence as soon as it exists rather than waiting for the full answer.
// ---------------------------------------------------------------------------
// Tails a candidate boundary must NOT split after: common abbreviations and
// list ordinals ("1.") — "e.g. apples" and "1. Check the run" are one breath.
const NO_SPLIT_TAIL = /(?:^|[\s(])(?:e\.g|i\.e|etc|no|approx|vs|\d{1,2})\.$/i

// A newline always ends a chunk (list-shaped answers rarely carry a
// terminator); otherwise ./!/? (+ optional closing quote) followed by
// whitespace ends one — unless the tail is an abbreviation/ordinal.
function sentenceBoundary(text: string): number {
  const re = /\n|[.!?]["')\]]?(?=\s)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    if (m[0] === '\n') return m.index
    const end = m.index + m[0].length
    if (!NO_SPLIT_TAIL.test(text.slice(0, end))) return end
  }
  return -1
}

export function chunkCompleteSentences(buffer: string): { complete: string[]; rest: string } {
  const complete: string[] = []
  let rest = buffer
  for (;;) {
    const cut = sentenceBoundary(rest)
    if (cut < 0) break
    const sentence = rest.slice(0, cut).trim()
    if (sentence) complete.push(sentence)
    rest = rest.slice(cut).replace(/^\s+/, '')
  }
  return { complete, rest }
}

// Markdown reads fine but speaks terribly — strip the markers in the SPEAK
// path only (rendered text is untouched): heading #, bullet prefixes,
// bold/italic markers, backticks, table pipes.
function stripMarkdownForSpeech(text: string): string {
  return text
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/[*_`|]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

// Locale-formatted digit groups ("1 010" — regular, no-break or narrow
// no-break space) get read as separate digits by some TTS engines — join them
// in the SPEAK path only (rendered text untouched). Joins only when EXACTLY
// three digits follow the separator ("on 21 July" and "rows 21 45" stay as
// they are); repeated because "1 234 567" needs a pass per separator.
const DIGIT_GROUP = /(\d)[ \u00A0\u202F](\d{3})(?!\d)/g
export function joinDigitGroupsForSpeech(text: string): string {
  let out = text
  for (;;) {
    const joined = out.replace(DIGIT_GROUP, '$1$2')
    if (joined === out) return out
    out = joined
  }
}

// The full speak-path transform \u2014 shared by the device speaker and the cloud
// TTS speaker so both engines pronounce identical text.
export function prepareTextForSpeech(text: string): string {
  return joinDigitGroupsForSpeech(stripMarkdownForSpeech(text))
}

// ---------------------------------------------------------------------------
// createSentenceSpeaker — feed() streamed text; complete sentences are queued
// into speechSynthesis immediately; flush() speaks the tail; cancel()
// silences. One speaker per answer; cancel on unmount/navigation/mute.
// ---------------------------------------------------------------------------
export type SentenceSpeaker = { feed: (delta: string) => void; flush: () => void; cancel: () => void }

export function listEnglishVoices(): SpeechSynthesisVoice[] {
  if (!speechOutputSupported()) return []
  return window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('en'))
}

// User-picked voice first, else prefer a local en-ZA voice, else any
// English one, else platform default. Within each language tier the
// higher-quality installs (Enhanced / Premium / Natural / Neural in the
// name) beat the default compact voice. getVoices() can legitimately be []
// until voiceschanged fires — a null voice uses the platform default.
const QUALITY_VOICE = /enhanced|premium|natural|neural/i
function bestVoice(voices: SpeechSynthesisVoice[], match: (v: SpeechSynthesisVoice) => boolean): SpeechSynthesisVoice | undefined {
  const tier = voices.filter(match)
  return tier.find(v => QUALITY_VOICE.test(v.name)) ?? tier[0]
}

function pickVoice(voiceName?: string): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices()
  return (voiceName ? voices.find(v => v.name === voiceName) : undefined)
    ?? bestVoice(voices, v => v.lang === 'en-ZA')
    ?? bestVoice(voices, v => v.lang.startsWith('en-GB'))
    ?? bestVoice(voices, v => v.lang.startsWith('en'))
    ?? null
}

export function createSentenceSpeaker(opts?: { rate?: number; voiceName?: string }): SentenceSpeaker {
  let buffer = ''
  let cancelled = false

  const speak = (text: string) => {
    const spoken = prepareTextForSpeech(text)
    if (!spoken) return
    const u = new SpeechSynthesisUtterance(spoken)
    u.voice = pickVoice(opts?.voiceName)
    u.rate = Math.min(1.6, Math.max(0.7, opts?.rate ?? 1.05))
    // resume() guards two engine wedges: Chrome's auto-pause on long queues
    // and Safari's paused state after a cancel().
    window.speechSynthesis.resume()
    window.speechSynthesis.speak(u)
  }

  return {
    feed(delta) {
      if (cancelled || !speechOutputSupported()) return
      buffer += delta
      const { complete, rest } = chunkCompleteSentences(buffer)
      buffer = rest
      complete.forEach(speak)
    },
    flush() {
      if (cancelled) return
      const tail = buffer.trim()
      buffer = ''
      if (tail) speak(tail)
    },
    cancel() {
      cancelled = true
      buffer = ''
      if (speechOutputSupported()) window.speechSynthesis.cancel()
    },
  }
}

// ---------------------------------------------------------------------------
// usePushToTalk — hold to capture one utterance. start() on pointer-down,
// stop() on release; the FINAL transcript fires onFinal exactly once, with
// the engine's recognition confidence where it measured one (0/undefined —
// engines that don't — arrive as undefined = UNKNOWN; several finalized
// segments report the worst of them). abort() (cancelled gesture / unmount)
// discards the transcript — a hold that didn't end in a deliberate release
// must never submit a question.
// ---------------------------------------------------------------------------
export function usePushToTalk(onFinal: (transcript: string, confidence?: number) => void): {
  supported: boolean
  listening: boolean
  interim: string
  error: string | null
  start: () => void
  stop: () => void
  abort: () => void
} {
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recRef = useRef<Recognition | null>(null)
  // Per-instance discard: engines deliver onend asynchronously after abort(),
  // so the discard must also make sure that late onend never fires onFinal
  // (a ghost ask from leftover interim).
  const discardRef = useRef<(() => void) | null>(null)
  const onFinalRef = useRef(onFinal); onFinalRef.current = onFinal

  useEffect(() => () => { discardRef.current?.() }, [])

  const start = () => {
    const Ctor = recognitionCtor()
    if (!Ctor) return
    // Rapid re-press: a previous instance whose onend hasn't fired yet must
    // not turn this start into a silent no-op — discard it and start fresh.
    if (recRef.current) { discardRef.current?.(); recRef.current = null }
    // Barge-in: pressing the mic silences whatever is still being spoken.
    if (speechOutputSupported()) window.speechSynthesis.cancel()
    setError(null); setInterim('')
    let finalText = ''
    let minConfidence: number | undefined
    // `live` mirrors the freshest transcript (final + interim): some engines
    // never mark a result final when stop() cuts recognition short, and onend
    // must not read stale state from the render start() was created in.
    let live = ''
    let discarded = false
    const rec = new Ctor()
    rec.lang = 'en-ZA'
    rec.continuous = true
    rec.interimResults = true
    rec.onresult = (e) => {
      if (discarded) return
      let interimText = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal) {
          finalText += r[0].transcript
          // 0/undefined = the engine didn't really measure — stays unknown.
          const c = r[0].confidence
          if (typeof c === 'number' && c > 0) minConfidence = minConfidence === undefined ? c : Math.min(minConfidence, c)
        } else interimText += r[0].transcript
      }
      live = (finalText + ' ' + interimText).replace(/\s+/g, ' ').trim()
      setInterim(live)
    }
    rec.onerror = (e) => {
      if (discarded) return
      // 'aborted' is our own discard; everything else gets an honest,
      // specific line ('no-speech' on a too-quick release keeps the generic).
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError('Microphone permission is blocked — allow it in your browser settings.')
      } else if (e.error === 'network') {
        setError('Voice input needs an internet connection.')
      } else if (e.error === 'audio-capture') {
        setError('No microphone found — check your device.')
      } else if (e.error !== 'aborted') {
        setError('Could not hear that — try again.')
      }
    }
    rec.onend = () => {
      if (recRef.current === rec) { recRef.current = null; setListening(false); setInterim('') }
      if (!discarded && live) onFinalRef.current(live, minConfidence)
    }
    discardRef.current = () => {
      discarded = true
      try { rec.abort() } catch { /* already ended */ }
      if (recRef.current === rec) { recRef.current = null; setListening(false); setInterim('') }
    }
    recRef.current = rec
    setListening(true)
    try { rec.start() } catch { recRef.current = null; setListening(false) }
  }

  const stop = () => { recRef.current?.stop() }
  const abort = () => { discardRef.current?.() }

  return { supported: speechInputSupported(), listening, interim, error, start, stop, abort }
}
